import React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

const ServiceCard = ({ service, index }) => {
  const navigate = useNavigate();

  const Icon = service.icon;

  const handleExplore = () => {
    navigate(service.link);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{
        duration: 0.6,
        delay: index * 0.08,
        ease: "easeOut",
      }}
      whileHover={{
        y: -8,
      }}
      className="
        group
        relative
        flex
        h-full
        min-h-[360px]
        w-full
        flex-col
        overflow-hidden
        rounded-2xl
        border
        transition-all
        duration-300
        hover:border-orange-500/40
        hover:shadow-[0_20px_60px_rgba(249,115,22,0.08)]
        sm:rounded-3xl
      "
      style={{
        backgroundColor: "var(--card-bg)",
        borderColor: "var(--border-primary)",
      }}
    >
      {/* =====================================================
          BACKGROUND GLOW
      ====================================================== */}

      <div
        className="
          pointer-events-none
          absolute
          -right-20
          -top-20
          h-40
          w-40
          rounded-full
          bg-orange-500/10
          opacity-0
          blur-3xl
          transition-opacity
          duration-500
          group-hover:opacity-100
          sm:h-52
          sm:w-52
        "
      />

      {/* =====================================================
          BOTTOM GLOW
      ====================================================== */}

      <div
        className="
          pointer-events-none
          absolute
          -bottom-24
          -left-20
          h-44
          w-44
          rounded-full
          bg-orange-500/5
          opacity-0
          blur-3xl
          transition-opacity
          duration-500
          group-hover:opacity-100
        "
      />

      {/* =====================================================
          TOP ORANGE LINE
      ====================================================== */}

      <div
        className="
          absolute
          left-0
          top-0
          h-[2px]
          w-0
          transition-all
          duration-500
          group-hover:w-full
        "
        style={{
          backgroundColor: "var(--brand-primary)",
        }}
      />

      {/* =====================================================
          CARD CONTENT
      ====================================================== */}

      <div
        className="
          relative
          z-10
          flex
          h-full
          flex-col
          p-5
          sm:p-6
          md:p-7
          lg:p-8
        "
      >
        {/* =================================================
            ICON
        ================================================== */}

        <div
          className="
            mb-5
            flex
            h-12
            w-12
            shrink-0
            items-center
            justify-center
            rounded-xl
            bg-orange-500/10
            transition-all
            duration-300
            group-hover:bg-orange-500
            group-hover:text-white
            sm:mb-6
            sm:h-14
            sm:w-14
            sm:rounded-2xl
            md:h-16
            md:w-16
          "
          style={{
            color: "var(--brand-primary)",
          }}
        >
          <Icon
            size={24}
            strokeWidth={1.8}
            className="
              transition-transform
              duration-300
              group-hover:scale-110
              sm:h-7
              sm:w-7
              md:h-[30px]
              md:w-[30px]
            "
          />
        </div>

        {/* =================================================
            TITLE
        ================================================== */}

        <h3
          className="
            font-saira
            text-xl
            font-bold
            leading-tight
            transition-colors
            duration-300
            group-hover:text-orange-400
            sm:text-2xl
          "
          style={{
            color: "var(--text-primary)",
          }}
        >
          {service.title}
        </h3>

        {/* =================================================
            DESCRIPTION
        ================================================== */}

        <p
          className="
            mt-4
            line-clamp-4
            text-sm
            leading-6
            sm:mt-5
            sm:text-base
            sm:leading-7
            md:leading-8
          "
          style={{
            color: "var(--text-secondary)",
          }}
        >
          {service.description}
        </p>

        {/* =================================================
            FEATURES
        ================================================== */}

        <div className="mt-5 space-y-2.5 sm:mt-6">
          {service.features?.map((feature, featureIndex) => (
            <div
              key={featureIndex}
              className="flex items-start gap-2.5"
            >
              {/* Feature Bullet */}

              <span
                className="
                  mt-[7px]
                  h-1
                  w-1
                  shrink-0
                  rounded-full
                "
                style={{
                  backgroundColor: "var(--brand-primary)",
                }}
              />

              {/* Feature Text */}

              <span
                className="
                  text-xs
                  leading-5
                  sm:text-sm
                "
                style={{
                  color: "var(--text-secondary)",
                }}
              >
                {feature}
              </span>
            </div>
          ))}
        </div>

        {/* =================================================
            EXPLORE BUTTON
        ================================================== */}

        <div className="mt-auto pt-6 sm:pt-8">
          <button
            type="button"
            onClick={handleExplore}
            className="
              group/link
              inline-flex
              max-w-full
              items-center
              gap-2
              text-sm
              font-semibold
              transition-all
              duration-300
              sm:gap-3
              sm:text-base
            "
            style={{
              color: "var(--brand-light)",
            }}
          >
            <span className="truncate">
              Explore Service
            </span>

            <ArrowRight
              size={17}
              className="
                shrink-0
                transition-transform
                duration-300
                group-hover/link:translate-x-1
                sm:h-[18px]
                sm:w-[18px]
              "
            />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default ServiceCard;